/** /tour_operators/configs/ — batch tenant configuration.
 *
 * Three endpoints backing the batch config grid: the combined runtime schema
 * bundle, every tenant's full (secret-masked) config in one response, and the
 * all-or-nothing multi-tenant batch persist. Types mirror the
 * `TourOperatorConfig*` components of `apiv2/console.openapi.yaml`.
 */
import type { SchemaProp } from '@/features/tour-operators/config/schema-grid/types';

import { apiGet, apiPost, ConsoleApiError } from './client';

/** Sentinel the backend substitutes for secret values on read; submitting it
 *  back through the batch endpoint keeps the stored secret unchanged. Mirrors
 *  backend `apiv2/serializers/console/integrations.py`; contract-tested
 *  against the spec's `x-masked-secret` vendor key. */
export const MASKED_SECRET = '••••••••';

/** One tenant's row of `TourOperatorConfigBulkList.results`. */
export interface TenantConfigItem {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  parent_id: number | null;
  /** Full config with secrets masked by the `MASKED_SECRET` sentinel. */
  config: Record<string, unknown> | null;
  /** Per-tenant `if_unmodified_since` baseline for the batch endpoint
   *  (null = no change history yet). */
  config_last_modified_ts: string | null;
}

export interface TenantConfigList {
  count: number;
  results: TenantConfigItem[];
}

/** Minimal structural type for a served JSON-schema object node. The per-prop
 *  shape is the schema-grid `SchemaProp`; everything else stays open. */
export interface SchemaObjectLike {
  properties?: Record<string, SchemaProp>;
  [k: string]: unknown;
}

/** `TourOperatorConfigSchemaBundle` — `{top, integrations}` where
 *  `integrations` maps restech name → composed integration schema. */
export interface ConfigSchemaBundle {
  top: SchemaObjectLike;
  integrations: Record<string, SchemaObjectLike>;
}

/** One tenant's entry of the batch persist body. Paths use the
 *  `segment` / `segment[N]` grammar (concrete list indices only — the client
 *  resolves `[*]` wildcards before submitting). */
export interface BatchTenantChange {
  tour_operator: number;
  /** REQUIRED key (nullable): the `config_last_modified_ts` captured at load;
   *  `null` means the tenant had no history at load. */
  if_unmodified_since: string | null;
  /** `{path: newValue}` — applied before `unset`. Values equal to the mask
   *  sentinel keep the stored secret. */
  set?: Record<string, unknown>;
  /** Paths to remove (dict keys only — list elements are rejected). */
  unset?: string[];
}

export interface BatchPersistRequest {
  comment: string;
  changes: BatchTenantChange[];
}

export interface BatchPersistResultItem {
  tour_operator: number;
  /** New `Tour_Operator_Config_Change` row id (null for no-ops). */
  change_id: number | null;
  changed_paths: string[];
  /** The tenant's new concurrency baseline after this batch. */
  config_last_modified_ts: string | null;
  noop: boolean;
}

export interface BatchPersistResponse {
  comment: string;
  applied: number;
  noops: number;
  results: BatchPersistResultItem[];
}

export interface BatchConflictEntry {
  tour_operator: number;
  code: 'config_modified' | 'not_found' | 'invalid_path';
  detail: string;
  /** Present for `invalid_path` errors. */
  path?: string;
  /** Present for `config_modified` errors. */
  current_config_last_modified_ts?: string;
}

/** 409 body — all-or-nothing: a conflict means NOTHING was persisted. */
export interface BatchConflictError {
  detail: string;
  code: 'batch_conflict';
  errors: BatchConflictEntry[];
}

/** GET /tour_operators/configs/ — every tenant's full config, one response.
 *  ~200 tenants with full configs: allow well beyond the default timeout. */
export function listAllConfigs(): Promise<TenantConfigList> {
  return apiGet<TenantConfigList>('/tour_operators/configs/', { timeout: 60_000 });
}

/** GET /tour_operators/configs/schema/ — combined runtime schema bundle. */
export function getConfigSchemaBundle(): Promise<ConfigSchemaBundle> {
  return apiGet<ConfigSchemaBundle>('/tour_operators/configs/schema/');
}

/** POST /tour_operators/configs/batch/ — all-or-nothing multi-tenant persist. */
export function persistBatch(body: BatchPersistRequest): Promise<BatchPersistResponse> {
  return apiPost<BatchPersistResponse>('/tour_operators/configs/batch/', body);
}

/** Narrow an unknown error to the batch endpoint's 409 `batch_conflict`. */
export function isBatchConflict(
  err: unknown,
): err is ConsoleApiError & { body: BatchConflictError } {
  return (
    err instanceof ConsoleApiError &&
    err.status === 409 &&
    (err.body as { code?: unknown } | undefined)?.code === 'batch_conflict'
  );
}
